import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FeedbackForm } from '../components/FeedbackForm';
import { ToastStack } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import type { OrganizationInfo, WidgetAuth } from '../types/api';
import { thankYouCopy } from '../config/copy';

interface ThankYouScreenProps {
  organization: OrganizationInfo;
  callId: number;
  auth?: WidgetAuth;
}

export function ThankYouScreen({ organization, callId, auth }: ThankYouScreenProps) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Toast system (independent stack for ThankYouScreen)
  const { toasts, addToast, removeToast } = useToast();

  const handleFeedbackSubmit = () => {
    setShowFeedback(false);
    setFeedbackSubmitted(true);
  };

  const handleFeedbackError = useCallback((error: string) => {
    addToast({
      type: 'error',
      title: 'Feedback Not Sent',
      message: error || 'Please try again.',
      action: {
        label: 'Retry',
        onClick: () => setShowFeedback(true),
      },
    });
  }, [addToast]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-brand-cream flex flex-col items-center justify-center px-6"
    >
      {/* Toast notifications */}
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      {/* Thank you message */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.5 }}
        className="w-20 h-20 rounded-full bg-brand-gold/10 flex items-center justify-center mb-6"
      >
        <svg className="w-10 h-10 text-brand-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </motion.div>

      <motion.h1
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-3xl font-serif text-brand-navy mb-2 text-center"
      >
        {thankYouCopy.heading}
      </motion.h1>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-gray-600 text-center mb-8 max-w-md"
      >
        {thankYouCopy.bodyTemplate(organization.name)}
      </motion.p>

      {/* Feedback button or success message */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        {feedbackSubmitted ? (
          <div className="text-center">
            <p className="text-brand-gold font-medium mb-2">{thankYouCopy.feedbackSuccess.primary}</p>
            <p className="text-sm text-gray-500">{thankYouCopy.feedbackSuccess.secondary}</p>
          </div>
        ) : (
          <button
            onClick={() => setShowFeedback(true)}
            className="px-8 py-4 bg-brand-gold text-white font-medium rounded-lg shadow-lg hover:bg-brand-gold-dark transition-colors"
          >
            {thankYouCopy.cta}
          </button>
        )}
      </motion.div>

      {/* Feedback modal */}
      <AnimatePresence>
        {showFeedback && (
          <FeedbackForm
            callId={callId}
            onClose={() => setShowFeedback(false)}
            onSubmit={handleFeedbackSubmit}
            onError={handleFeedbackError}
            auth={auth}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
